import { useTranslation } from 'react-i18next';

import { Text } from '../../../../../../components/atoms/text/text';
import { useAllRespondentTypes } from '../../../../context/render-context';
import { Answers, Survey } from '../../../../survey-types';
import { ConditionalAnswer } from '../../../../utils/surveys/conditional-answers';
import { getQuestionsToShow } from '../../../../utils/surveys/questions';
import { getAnswerLabel, shouldShowNotCompleted } from '../../../../utils/surveys/report-labels';
import { StatusIcon } from '../icons/icons';
import { IAnswerElementProps } from '../types';

import { QuestionItem } from './question-item';

interface IRenderQuestionItemsProps {
  questionsToShow: Array<{ question: string }>;
  answers: Answers;
  detailed: string;
  survey: Survey;
  truncateTo: number;
  link?: string;
  externalLink?: string;
}

const QuestionItems = ({
  questionsToShow,
  answers,
  detailed,
  survey,
  truncateTo,
  link,
  externalLink,
}: IRenderQuestionItemsProps) => {
  return questionsToShow.map((questionItem, index) => (
    <QuestionItem
      answers={answers}
      detailed={detailed}
      externalLink={externalLink}
      index={index}
      key={`${questionItem.question}-${index}`}
      link={link}
      questionItem={questionItem}
      survey={survey}
      truncateTo={truncateTo}
    />
  ));
};

interface IRenderQuestionsContentProps {
  readonly detailed: string;
  readonly selectedQuestions: Array<{ question: string }>;
  readonly answers: Answers;
  readonly survey: Survey;
  readonly truncateTo: number;
  readonly allNotShown: boolean;
  readonly hasAnyAnswer: boolean;
  readonly link?: string;
  readonly externalLink?: string;
}

const QuestionsContent = ({
  detailed,
  selectedQuestions,
  answers,
  survey,
  truncateTo,
  allNotShown,
  hasAnyAnswer,
  link,
  externalLink,
}: IRenderQuestionsContentProps) => {
  const { t } = useTranslation();
  const respondentTypes = useAllRespondentTypes();
  const multipleRespondents = respondentTypes.length > 1;
  const questionsToShow = getQuestionsToShow(detailed, selectedQuestions, answers);

  if (questionsToShow.length === 0 || allNotShown || !hasAnyAnswer) {
    const showNotCompleted = shouldShowNotCompleted(multipleRespondents, answers);
    const label = getAnswerLabel(showNotCompleted, allNotShown) ?? 'report.answerSkipped';

    return (
      <div className="flex-1 justify-start">
        {}
        <Text className="free-text-inactive">{t(label)}</Text>
      </div>
    );
  }

  return (
    <QuestionItems
      answers={answers}
      detailed={detailed}
      externalLink={externalLink}
      link={link}
      questionsToShow={questionsToShow}
      survey={survey}
      truncateTo={truncateTo}
    ></QuestionItems>
  );
};

export const AnswerElement = ({
  answers,
  detailed,
  selectedQuestions,
  survey,
  title,
  truncateTo,
  link,
  externalLink,
}: IAnswerElementProps) => {
  const releventAnswersCopy: { [key: string]: ConditionalAnswer } = {};
  for (const q of selectedQuestions) {
    releventAnswersCopy[q.question] = new ConditionalAnswer(
      survey.model.getQuestionByName(q.question),
      answers[q.question]
    );
  }

  const allNotShown = selectedQuestions.every((q) => {
    return releventAnswersCopy[q.question].isNotShown;
  });
  const hasAnyAnswer = selectedQuestions.some((questionItem) => {
    return !releventAnswersCopy[questionItem.question].isSkipped;
  });

  return (
    <div className="flex items-start gap-2">
      <div className="w-6 h-6 overflow-hidden">
        <StatusIcon hasAnswer={!allNotShown && hasAnyAnswer} />
      </div>
      <div className="flex-1 pt-0.5 flex flex-col gap-1">
        <div className="text-slate-800 text-base font-semibold">{title}</div>
        {selectedQuestions.length > 0 && (
          <div className="flex flex-col gap-3">
            <QuestionsContent
              allNotShown={allNotShown}
              answers={answers}
              detailed={detailed}
              externalLink={externalLink}
              hasAnyAnswer={hasAnyAnswer}
              link={link}
              selectedQuestions={selectedQuestions}
              survey={survey}
              truncateTo={truncateTo}
            ></QuestionsContent>
          </div>
        )}
      </div>
    </div>
  );
};
